import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrMetadata } from '@nostrify/nostrify';

/**
 * Batch-fetches kind-0 metadata for many pubkeys in ONE relay query.
 * Use this instead of per-row useAuthor calls (N+1 round-trips).
 */
export function useAuthors(pubkeys: string[] | undefined) {
  const { nostr } = useNostr();

  const unique = [...new Set((pubkeys ?? []).filter(Boolean))];

  return useQuery({
    queryKey: ['nostr', 'authors', unique.join(',')],
    queryFn: async (c) => {
      if (unique.length === 0) return new Map<string, NostrMetadata>();
      const events = await nostr.query(
        [{ kinds: [0], authors: unique, limit: unique.length }],
        { signal: c.signal },
      );
      const map = new Map<string, NostrMetadata>();
      for (const e of events) {
        try {
          map.set(e.pubkey, JSON.parse(e.content) as NostrMetadata);
        } catch {
          // ignore malformed metadata
        }
      }
      return map;
    },
    staleTime: 5 * 60 * 1000,
    enabled: unique.length > 0,
  });
}
